/**
 * Prijsverwerking (US-019).
 *
 * Prijzen worden intern altijd als integer eurocenten bewaard. De oorspronkelijke prijstekst
 * blijft daarnaast opgeslagen, zodat iedere meting controleerbaar is.
 */
/** Nederlandse weergave: "€ 1.234,56". */
export function formatEuro(cents) {
    if (cents === null || cents === undefined)
        return '';
    const negative = cents < 0;
    const abs = Math.abs(Math.round(cents));
    const euros = Math.floor(abs / 100);
    const rest = abs % 100;
    const grouped = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${negative ? '-' : ''}€ ${grouped},${String(rest).padStart(2, '0')}`;
}
/** Verschilweergave met expliciet teken: "+ € 0,30" / "− € 0,55". */
export function formatDelta(cents) {
    const sign = cents > 0 ? '+' : cents < 0 ? '−' : '';
    return `${sign} ${formatEuro(Math.abs(cents))}`.trim();
}
/** Percentageweergave met NL-decimaalteken: "+2,8%" / "-1,5%". */
export function formatPercent(value, decimals = 1) {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(decimals).replace('.', ',')}%`;
}
/**
 * Procentueel verschil van een concurrentprijs ten opzichte van de eigen prijs (US-022).
 * Negatief = concurrent is goedkoper. Zonder geldige eigen prijs wordt niets berekend.
 */
export function percentVsOwn(competitorCents, ownCents) {
    if (competitorCents === null || ownCents === null)
        return null;
    if (ownCents <= 0)
        return null;
    return ((competitorCents - ownCents) / ownCents) * 100;
}
/**
 * De vorm van een bedrag met duizendtalgroepen: "1.234", "12,345,678". Een groep telt precies drie
 * cijfers en het getal ervóór begint niet met een nul.
 */
const DUIZENDTALLEN = /^-?[1-9]\d{0,2}(?:[.,]\d{3})+$/;
/**
 * Zet een prijstekst van een webpagina om naar eurocenten (US-019).
 *
 * Ondersteunt de gangbare notaties: "€ 1.234,56", "1234,56", "€1,234.56", "35,95 EUR", "€ 35,-".
 * Retourneert `null` wanneer geen eenduidig bedrag kan worden vastgesteld — er wordt dan
 * bewust géén € 0,00 opgeslagen.
 */
export function parsePriceToCents(input) {
    if (!input)
        return null;
    const raw = input.trim();
    if (!raw)
        return null;
    // Alleen cijfers en scheidingstekens overhouden; valuta-aanduidingen en spaties eromheen weg.
    const cleaned = raw
        .replace(/\u00a0/g, ' ')
        .replace(/(eur|euro|€)/gi, ' ')
        .replace(/[^\d.,-]/g, ' ')
        .trim()
        /*
         * Witruimte tussen een bedrag en zijn centen weghalen (US-019).
         *
         * Webshops zetten de centen vaak in een eigen element:
         * <span>87</span><span>,</span><span>33</span>. Staat er in de HTML een regeleinde of een
         * spatie tussen die elementen — en dat is bij opgemaakte HTML het normale geval — dan leest
         * de tekst als "87 , 33".
         *
         * Dit is geen nette-vorm-kwestie maar een verkeerde prijs. Zonder deze regel pakte de match
         * hieronder alleen "87" en werd er **€ 87,00** vastgelegd in plaats van € 87,33: geen
         * mislukte meting, maar een geloofwaardig getal dat gewoon fout is. Dat is het ergste soort
         * fout in deze applicatie, want er is niets aan te zien.
         *
         * Bewust nauw: er moeten precies twee cijfers achter het scheidingsteken staan en er mag
         * geen derde volgen. Anders zou "Lengte 6, 143 mm" tot "6,143" worden geplakt, en dat is
         * een duizendtalgroep — een heel ander getal.
         */
        .replace(/(\d)[ \t\n\r]*([.,])[ \t\n\r]*(\d{2})(?!\d)/g, '$1$2$3');
    // "35,-" en "35,--" betekenen 35 euro rond.
    const roundMatch = cleaned.match(/^(\d{1,3}(?:[.\s]\d{3})*|\d+),-{1,2}$/);
    if (roundMatch?.[1]) {
        const euros = Number(roundMatch[1].replace(/[.\s]/g, ''));
        if (!Number.isFinite(euros))
            return null;
        return { cents: euros * 100, raw };
    }
    /**
     * Eén bedrag, en niets eromheen. Eerder werd hier een reeks van cijfers, punten, komma's én
     * spaties gepakt; bij "69,18 per 6 meter" bleef daardoor "69,186" over — € 69,19 in plaats
     * van € 69,18. Een scheidingsteken hoort er alleen bij wanneer het een duizendtalgroep van
     * precies drie cijfers inleidt, of decimalen.
     *
     * De eerste variant leest een bedrag met duizendtalgroepen, de tweede een gewoon bedrag met
     * eventuele decimalen. Twee eisen aan de eerste zijn er later bij gekomen, en beide voorkomen
     * een uitkomst die een factor duizend te hoog is:
     *
     *  - een duizendtalgroep begint niet met een nul, dus "0.999" is nooit 999;
     *  - achter de laatste groep mag geen cijfer meer komen. Een XML-feed leverde
     *    `<g:price>106.735431 EUR</g:price>`, en daar werd "106.735" uit gelezen en als
     *    duizendtalgroep geduid: **€ 106.735,00** in plaats van € 106,74. Net als bij de centen in
     *    een eigen element is dat geen mislukte meting maar een geloofwaardig fout getal.
     *
     * Meer dan twee decimalen worden dus gelezen en daarna afgerond. Een feed die met zes decimalen
     * rekent is geen reden om de meting te laten mislukken.
     */
    const numberMatch = cleaned.match(/-?[1-9]\d{0,2}(?:[.,\s]\d{3})+(?:[.,]\d{1,2})?(?![.,]?\d)|-?\d+(?:[.,]\d+)?/);
    if (!numberMatch)
        return null;
    const token = numberMatch[0].replace(/\s/g, '');
    /*
     * Het laatste scheidingsteken bepaalt de duiding: drie cijfers erachter is een duizendtalgroep
     * ("1.234" en "1,234" zijn beide 1234), anders zijn het decimalen. Zo maakt het niet uit of een
     * webshop de Nederlandse of de Engelse notatie gebruikt.
     *
     * Die duiding wordt aan de vorm van het hele getal getoetst en niet alleen aan het aantal
     * cijfers achter het laatste teken, want anders wijkt hij af van wat hierboven is gevonden:
     * "0,999" heeft drie cijfers achter de komma maar is geen duizendtalgroep.
     */
    const laatsteScheiding = Math.max(token.lastIndexOf(','), token.lastIndexOf('.'));
    let normalized;
    if (laatsteScheiding === -1) {
        normalized = token;
    }
    else if (DUIZENDTALLEN.test(token)) {
        normalized = token.replace(/[.,]/g, '');
    }
    else {
        const heleGetal = token.slice(0, laatsteScheiding).replace(/[.,]/g, '');
        normalized = `${heleGetal}.${token.slice(laatsteScheiding + 1)}`;
    }
    const value = Number(normalized);
    if (!Number.isFinite(value))
        return null;
    const cents = Math.round(value * 100);
    if (cents === 0)
        return null; // Geen bedrag van € 0,00 opslaan (US-019).
    return { cents, raw };
}
export function determineChange(newCents, previousValidCents) {
    if (newCents === null) {
        // Lege of foutieve metingen zijn nooit een wijziging (US-017/021).
        return { isFirstMeasurement: false, isChange: false, changeCents: null, direction: 'NONE' };
    }
    if (previousValidCents === null) {
        return { isFirstMeasurement: true, isChange: false, changeCents: null, direction: 'NONE' };
    }
    const delta = newCents - previousValidCents;
    if (Math.abs(delta) < 1) {
        return { isFirstMeasurement: false, isChange: false, changeCents: 0, direction: 'NONE' };
    }
    return {
        isFirstMeasurement: false,
        isChange: true,
        changeCents: delta,
        direction: delta > 0 ? 'UP' : 'DOWN',
    };
}
/** Bewijs vastleggen bij een wijziging van méér dan 5% (US-028). Exact 5% is niet verplicht. */
export function requiresEvidence(newCents, previousValidCents) {
    if (newCents === null || previousValidCents === null || previousValidCents === 0)
        return false;
    const pct = (Math.abs(newCents - previousValidCents) / previousValidCents) * 100;
    return pct > 5;
}
/**
 * Beoordeelt of een tekst bevestigt dat de prijs inclusief btw is.
 *
 * Er wordt uitsluitend gekeken naar uitspraken *over btw*. Eerder sloeg de controle al aan op
 * het losse woord "excl", waardoor "incl. btw (Excl. verzendkosten)" werd afgekeurd: dat "excl"
 * gaat over verzendkosten en zegt niets over de btw.
 */
export function vatConfirmation(text) {
    if (!text?.trim()) {
        return { status: 'ONBEKEND', reason: 'Het aangewezen element bevat geen tekst.' };
    }
    const t = text.toLowerCase().replace(/\u00a0/g, ' ');
    const btw = '(?:btw|b\\.t\\.w|vat|tax)';
    // Tussenruimte beperkt tot een handvol tekens, zodat "excl. verzendkosten … incl. btw" niet
    // als één uitspraak wordt gelezen.
    const exclusief = new RegExp(`(?:excl\\.?|exclusief|zonder|ex\\.)[^a-z0-9]{0,4}${btw}`).test(t);
    const inclusief = new RegExp(`(?:incl\\.?|inclusief|met)[^a-z0-9]{0,4}${btw}`).test(t) ||
        new RegExp(`${btw}[^a-z0-9]{0,4}(?:inbegrepen|included|inclusief)`).test(t);
    if (inclusief && exclusief) {
        return {
            status: 'TEGENSTRIJDIG',
            reason: 'De tekst noemt zowel een prijs inclusief als exclusief btw; niet vast te stellen welke bij het prijselement hoort.',
        };
    }
    if (exclusief)
        return { status: 'EXCLUSIEF', reason: 'De tekst zegt dat de prijs exclusief btw is.' };
    if (inclusief)
        return { status: 'INCLUSIEF', reason: 'De tekst bevestigt een prijs inclusief btw.' };
    return { status: 'ONBEKEND', reason: 'De tekst zegt niets over btw.' };
}
export const VAT_MODE_LABELS = {
    ASSUME_INCLUSIVE: 'Prijs is inclusief btw (aannemen)',
    CONFIRM_ON_PAGE: 'Bevestiging op de pagina vereisen',
    PRICE_IS_EXCLUSIVE: 'Prijs is exclusief btw (omrekenen)',
};
/** Het gangbare Nederlandse btw-tarief; per bron aanpasbaar voor een afwijkend tarief. */
export const DEFAULT_VAT_RATE_PERCENT = 21;
/** Telt btw bij een exclusieve prijs op. Afronding op de eurocent, zoals elke webshop doet. */
export function addVat(cents, ratePercent) {
    return Math.round((cents * (100 + ratePercent)) / 100);
}
/**
 * Rekent de brutoprijs uit de feed om naar de werkelijke nettoprijs.
 *
 * Voor twee bedrijven staat in de feed een brutoprijs die de webshop nu ten onrechte als
 * nettoprijs toont. Zolang op het product geen correctiepercentage is ingevuld, blijft de
 * feedprijs ongewijzigd (`percent` is `null` of `undefined`) — dat is het gedrag van vóór deze
 * correctie en geldt voor alle andere bedrijven.
 */
export function applyNettoPercentage(cents, percent) {
    if (percent === null || percent === undefined)
        return cents;
    return Math.round((cents * (100 - percent)) / 100);
}
//# sourceMappingURL=money.js.map